import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentArrowDownIcon, FunnelIcon, ExclamationTriangleIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import ButtonComponent from 'components/common/ButtonComponent'
import TableComponent from 'components/common/TableComponent'
import ModalComponent from 'components/common/ModalComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import { useAuth } from 'auth/AuthContext'
import { DateTime } from 'luxon'
import DateRangeFilterModal from 'components/modals/DateRangeFilterModal'
import { useDashboardDateRange } from 'hooks/useDashboardDateRange'
import { useDashboardFilterOptions } from 'hooks/useDashboardFilterOptions'
import { buildDashboardDateRangeQuery, getDateRangeSummary } from 'utils/dateRangeFilters'
import { exportDataEditExcel, exportDataEditPdf, filterRowsBySearch } from 'utils/dataEditExport'

const exportColumns = [
  { key: 'workerName', label: 'Worker' }, { key: 'date', label: 'Date' },
  { key: 'startTime', label: 'Start Time' }, { key: 'endTime', label: 'End Time' },
  { key: 'edited', label: 'Edited' }
]

export default function TimingDataEdit () {
  const hasFetched = useRef(false)
  const currentFilter = useRef(null)
  const { user } = useAuth()
  const { t } = useTranslation()
  const currentDate = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')
  const [dateRange, setDateRange] = useDashboardDateRange(currentDate)
  const filterOptions = useDashboardFilterOptions(user?.farmId)
  const [showFilters, setShowFilters] = useState(false)
  const [state, setState] = useState({
    workdays: [],
    searchTerm: '',
    showEditModal: false,
    selectedWorkday: null,
    showDeleteConfirm: false,
    pendingDelete: null,
    loading: true
  })

  const getWorkdays = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/workdays/${user.farmId}?${buildDashboardDateRangeQuery(dateRange)}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      setState(s => ({ ...s, workdays: response.data, loading: false }))
    } catch (error) {
      console.error('Error:', error)
      setState(s => ({ ...s, loading: false }))
    }
  }

  const openEditModal = (row) => {
    setState(s => ({ ...s, showEditModal: true, selectedWorkday: row }))
  }

  const closeEditModal = () => {
    setState(s => ({ ...s, showEditModal: false, selectedWorkday: null }))
  }

  const handleSaveWorkday = async (formData) => {
    try {
      await makeRequest({
        method: 'PATCH',
        url: `/management/workdays/${state.selectedWorkday.workdayId}`,
        data: {
          startTime: formData.startTime || undefined,
          endTime: formData.endTime || undefined
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: t('mgmt.timingDataEdit.updated'), icon: 'success' })
      closeEditModal()
      await getWorkdays()
    } catch (error) {
      showAlert({ text: t('mgmt.timingDataEdit.updateFailed'), icon: 'error' })
      console.error('Error updating workday:', error)
    }
  }

  const handleDelete = (row) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: row }))
  }

  const confirmDelete = async () => {
    const row = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/workdays/${row.workdayId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      })
      showAlert({ text: t('mgmt.timingDataEdit.deleted'), icon: 'success' })
      await getWorkdays()
    } catch (error) {
      showAlert({ text: t('mgmt.timingDataEdit.deleteFailed'), icon: 'error' })
    }
  }

  const filteredWorkdays = filterRowsBySearch(state.workdays, state.searchTerm)

  useEffect(() => {
    if (!user?.farmId) return
    const nextFilter = JSON.stringify(dateRange)
    if (hasFetched.current && currentFilter.current === nextFilter) return
    hasFetched.current = true
    currentFilter.current = nextFilter
    getWorkdays()
  }, [user?.farmId, dateRange])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.timingDataEdit.loading')} rows={8} />
  }

  return (
    <div className='p-6 bg-white shadow-md border border-gray-300 border-t-0'>
      <div className='section-header'>
        <div className='header-left'>
          <div className='date-filter'>
            <span>{getDateRangeSummary(dateRange)}</span>
          </div>
        </div>
        <div className='flex items-center gap-4 mb-6'>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditExcel({ rows: filteredWorkdays, columns: exportColumns, fileName: `timing-data-${currentDate}`, sheetName: 'Timing Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('common.exportExcel')}</span>
          </ButtonComponent>
          <ButtonComponent className='flex items-center gap-2' onClick={() => exportDataEditPdf({ rows: filteredWorkdays, columns: exportColumns, fileName: `timing-data-${currentDate}`, title: 'Timing Data' })}>
            <DocumentArrowDownIcon className='w-5 h-5' />
            <span>{t('mgmt.common.exportPdf')}</span>
          </ButtonComponent>
        </div>
      </div>

      <div className='search-container flex gap-4 mb-4'>
        <input
          type='text'
          placeholder={t('mgmt.dataEdit.search')}
          value={state.searchTerm}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='w-48 mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent className='flex items-center gap-2' onClick={() => setShowFilters(true)}><FunnelIcon className='w-5 h-5' /><span>{t('dashboard.advancedFilters')}</span></ButtonComponent>
      </div>

      <div className='data-table'>
        <TableComponent
          tableData={{
            columns: [
              { key: 'workerName', labelKey: 'mgmt.dataEdit.cols.worker' },
              { key: 'date', labelKey: 'mgmt.dataEdit.cols.date', cell: (value) => parseDate(value) },
              { key: 'startTime', labelKey: 'mgmt.dataEdit.cols.startTime' },
              { key: 'endTime', labelKey: 'mgmt.dataEdit.cols.endTime' },
              { key: 'edited', labelKey: 'mgmt.dataEdit.cols.edited', cell: (value) => value ? t('mgmt.common.yes') : t('mgmt.common.no') },
              {
                key: 'actions',
                labelKey: 'mgmt.dataEdit.cols.manage',
                cell: (value, row) => (
                  <div className='flex items-center gap-2'>
                    <button onClick={() => openEditModal(row)} title={t('common.edit')} className='p-1 rounded transition'>
                      <PencilSquareIcon className='w-5 h-5 text-blue-500' />
                    </button>
                    <button onClick={() => handleDelete(row)} title={t('common.delete')} className='p-1 rounded transition'>
                      <TrashIcon className='w-5 h-5 text-red-500' />
                    </button>
                  </div>
                )
              }
            ],
            rows: filteredWorkdays
          }}
          defaultSortColumn='workerName'
        />
      </div>

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.timingDataEdit.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {state.pendingDelete?.date
              ? t('mgmt.timingDataEdit.removeConfirmWithDate', { name: state.pendingDelete?.workerName ?? '', date: state.pendingDelete.date })
              : t('mgmt.timingDataEdit.removeConfirm', { name: state.pendingDelete?.workerName ?? '' })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>

      {state.showEditModal && (
        <ModalComponent
          title={t('mgmt.timingDataEdit.editTitle')}
          isOpen={state.showEditModal}
          onClose={closeEditModal}
          onSave={handleSaveWorkday}
          initialData={{
            startTime: state.selectedWorkday?.startTime ?? '',
            endTime: state.selectedWorkday?.endTime ?? ''
          }}
        >
          {({ formData, handleChange }) => (
            <form className='space-y-4 w-64' autoComplete='off'>
              <p className='text-sm text-gray-600'>
                {t('mgmt.dataEditModal.worker')}: <strong>{state.selectedWorkday?.workerName}</strong>
              </p>
              <label className='block text-sm font-medium'>
                {t('mgmt.dataEdit.cols.startTime')}
                <input
                  type='time'
                  name='startTime'
                  value={formData?.startTime ?? ''}
                  onChange={handleChange}
                  className='w-full mt-1 p-2 border rounded'
                />
              </label>
              <label className='block text-sm font-medium'>
                {t('mgmt.dataEdit.cols.endTime')}
                <input
                  type='time'
                  name='endTime'
                  value={formData?.endTime ?? ''}
                  onChange={handleChange}
                  className='w-full mt-1 p-2 border rounded'
                />
              </label>
            </form>
          )}
        </ModalComponent>
      )}
      <DateRangeFilterModal isOpen={showFilters} dateRange={dateRange} sectors={filterOptions.sectors} farmingBlocks={filterOptions.farmingBlocks} groups={filterOptions.groups} activities={filterOptions.activities} maxDate={currentDate} onClose={() => setShowFilters(false)} onApply={(nextRange) => { setDateRange(nextRange); setShowFilters(false) }} />
    </div>
  )
}
